/* Infection & conversion (feature:convert).
   Infection: whoever a zombie kills in melee rises again after RISE_DELAY as a zombie of the killer's team. The new
   zombie takes a free slot of the zombie army's reserve (same blueprint, see convertRules: the client adds slots);
   chickens rise in place as zombie chickens instead.
   Conversion: high priests channel on their enemy target (more priests on one target help, with diminishing
   returns); when done, the
   target switches its team but keeps its type.
   A team change keeps the unit index (= type range + blueprint). The worker reads TEAM[i] for everything
   team-related (targets, grids, attacks) and the index range for stats, so the unit just fights for the other side.
   Pure helpers called by sim.worker.js; deterministic (no Math.random / trig, fixed iteration order).
   convertRules is shared with the client (setup options, reserve, which armies need recolouring). */

export const RISE_DELAY = 1.5;     // s from a zombie kill to rising again
export const CONVERT_CD = 3;       // s cooldown of the priest who completes a conversion
export const CHANNEL_RANGE = 9;    // m between priest and target (plus both radii)
export const KIND_RISE = 37, KIND_CONVERT = 38; // effect kinds in the 'convert' message (feature ID range 37-39)
const RISE_T = 0.9;                // s to climb out of the ground
const QCAP = 4096, FXCAP = 1024, BEAMCAP = 256, ADD_MAX = 30000;
const MACHINES = new Set(['panzer', 'mechhase', 'mech_pilot', 'optimus', 'megatron', 'roboter', 'robohund']);

const isZombie = a => a.recipe === 'zombie' || /zombie/i.test(a.name || '');
const isPriest = a => a.recipe === 'priester' || /priester/i.test(a.name || '');
const isMachine = a => MACHINES.has(a.key) || /panzer|mech|robo|optimus|megatron/i.test(a.name || '');

/** Setup rules. info[g] = { key, name, recipe, stats, count, team } per unit group (classic: two entries without team =
 *  team 0 and 1), reserve = building reserve per team, opt = { inf, conv }. Returns the worker options per group (null =
 *  feature idle), extra reserve slots per team (zombie teams need room for the risen: they land in the team's last group,
 *  so the client puts a zombie group last) and which groups the renderer has to prepare. */
export function convertRules(info, reserve, opt) {
  const G = info.length, team = info.map((a, g) => (a.team === 0 || a.team === 1 ? a.team : g ? 1 : 0));
  const zombie = info.map(isZombie), priest = info.map(isPriest);
  const zT = [0, 1].map(t => info.some((a, g) => team[g] === t && zombie[g])), pT = [0, 1].map(t => info.some((a, g) => team[g] === t && priest[g]));
  const rise = new Array(G).fill(0), chan = new Array(G).fill(0), add = [0, 0];
  for (let g = 0; g < G; g++) {
    const a = info[g], S = a.stats, t = team[g], e = 1 - t;
    const tough = !!S.giant || S.r >= 1.5 || isMachine(a); // sea monster, T-Rex, tanks, mechs
    if (opt.inf && zT[e] && !zombie[g] && !tough) rise[g] = a.key === 'huhn' ? 2 : 1;
    // brave units resist longer, fearless ones (bravery 9: zombies, monsters, size >= 3) not at all
    if (opt.conv && pT[e] && !tough && S.bravery < 9) chan[g] = Math.min(12, 2 * (1 + 3 * S.bravery) * Math.max(1, S.s || 1));
  }
  for (let g = 0; g < G; g++) if (rise[g] === 1) { const t = 1 - team[g]; add[t] = Math.min(ADD_MAX, add[t] + (info[g].count | 0)); }
  for (let t = 0; t < 2; t++) if (add[t] && reserve) add[t] = Math.min(ADD_MAX, add[t] + ((reserve[1 - t]) | 0));
  const riseT = [0, 1].map(t => info.some((a, g) => team[g] === t && rise[g] > 0)), chanT = [0, 1].map(t => info.some((a, g) => team[g] === t && chan[g] > 0));
  const on = rise.some(Boolean) || chan.some(Boolean);
  return {
    has: { zombie: zombie.some(Boolean), priest: priest.some(Boolean) },
    sim: on ? { rise, chan, zombie: info.map((a, g) => zombie[g] && riseT[1 - team[g]] ? 1 : 0), priest: info.map((a, g) => priest[g] && chanT[1 - team[g]] ? 1 : 0) } : null,
    reserveAdd: add,
    alt: info.map((a, g) => chan[g] > 0 || rise[g] === 2),                       // may show the other team's colours
    glow: info.map((a, g) => chan[g] > 0 || rise[g] === 2 || (zombie[g] && info.some((b, h) => team[h] !== team[g] && rise[h] === 1))),
  };
}

const typeOf = (w, i) => w.GRP[i]; // the unit's group (its type); w.army[g].team = the side it was set up for
const lim = (a, k, lo, hi) => { const v = Array.isArray(a) ? +a[k] : 0; return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : 0; };

/** Worker state for one battle; w = worker arrays and helpers (see cvWorld in sim.worker.js). null = idle. */
export function convertSetup(o, w) {
  if (!o || typeof o !== 'object') return null;
  const G = w.army.length, per = (a, lo, hi, int) => Array.from({ length: G }, (_, g) => (int ? lim(a, g, lo, hi) | 0 : lim(a, g, lo, hi)));
  const rise = per(o.rise, 0, 2, true), chan = per(o.chan, 0, 60, false), zombie = per(o.zombie, 0, 1, true), priest = per(o.priest, 0, 1, true);
  if (!rise.some(Boolean) && !chan.some(Boolean)) return null;
  const N = w.N;
  return {
    w, rise, chan, zombie, priest, t: 0,
    CH: new Int32Array(N).fill(-1), CC: new Float32Array(N),   // per priest: channel target, cooldown
    CP: new Float32Array(N), CS: new Int32Array(N).fill(-9), CN: new Uint8Array(N), n: 0, // per target: progress, last tick channelled, priests this tick
    // risers waiting (ring buffer, all share the same delay so it stays sorted by due time)
    QI: new Int32Array(QCAP), QA: new Uint8Array(QCAP), QT: new Uint8Array(QCAP), QK: new Uint8Array(QCAP),
    QX: new Float32Array(QCAP), QZ: new Float32Array(QCAP), QR: new Float32Array(QCAP), QD: new Float64Array(QCAP), qh: 0, qn: 0,
    RI: new Int32Array(QCAP), rN: 0, // units climbing out of the ground
    inf: [0, 0], conv: [0, 0],
    // output only (never read by the simulation): effects + "TEAM changed" for the renderer
    fx: new Float32Array(FXCAP * 6), fxN: 0, dirty: false, nDirty: false, beams: 0,
    mixed: false, fix: false,
  };
}

function fxPush(cv, kind, i, x, y, z, team) {
  if (cv.fxN >= FXCAP) return;
  const o = cv.fxN++ * 6; cv.fx[o] = kind; cv.fx[o + 1] = i; cv.fx[o + 2] = x; cv.fx[o + 3] = y; cv.fx[o + 4] = z; cv.fx[o + 5] = team;
}

/** damage() hook: unit j just died, k = melee attacker (-1 = other cause). */
export function convertDeath(cv, j, k) {
  const w = cv.w, ty = typeOf(w, j), home = w.army[ty].team;
  if (w.TEAM[j] !== home) { w.TEAM[j] = home; cv.dirty = true; } // a dead slot belongs to its own range again
  if (k < 0 || !cv.rise[ty] || !cv.zombie[typeOf(w, k)] || w.TEAM[k] === home || cv.qn >= QCAP) return;
  const q = (cv.qh + cv.qn++) % QCAP;
  cv.QI[q] = j; cv.QA[q] = typeOf(w, k); cv.QT[q] = w.TEAM[k]; cv.QK[q] = cv.rise[ty];
  cv.QX[q] = w.X[j]; cv.QZ[q] = w.Z[j]; cv.QR[q] = w.ROT[j]; cv.QD[q] = cv.t + RISE_DELAY;
}

// unit i fights for team tm from now on (keeps its index, type and hit points)
function switchTeam(cv, i, tm) {
  const w = cv.w;
  w.dead[w.TEAM[i]]++; w.spawned[tm]++; w.TEAM[i] = tm;
  w.TGT[i] = -1; w.PTGT[i] = -1; w.PEND[i] = 0; w.ENG[i] = 0; w.FLEE[i] = 0; w.RT[i] = 0;
  cv.CH[i] = -1; cv.CP[i] = 0; cv.CS[i] = -9;
  cv.mixed = cv.fix = cv.dirty = true;
}
function startRise(cv, i, h) {
  const w = cv.w;
  w.Y[i] = -h * 0.85; w.CD[i] = Math.max(w.CD[i], RISE_T); // buried, no attacks until out
  if (cv.rN < QCAP) cv.RI[cv.rN++] = i;
}

/** Once per tick, after the units moved (changes are picked up by the next tick's grids). */
export function convertStep(cv, dt) {
  const w = cv.w, X = w.X, Z = w.Z, ALIVE = w.ALIVE, TEAM = w.TEAM, army = w.army;
  cv.t += dt; cv.n++;
  // 1. the dead rise
  while (cv.qn > 0 && cv.QD[cv.qh] <= cv.t) {
    const q = cv.qh; cv.qh = (cv.qh + 1) % QCAP; cv.qn--;
    const tm = cv.QT[q];
    let i;
    if (cv.QK[q] === 2) {
      // zombie chicken: the same slot comes back for the zombie team (unless a barracks reused it meanwhile)
      i = cv.QI[q]; if (ALIVE[i]) continue;
      const ty = typeOf(w, i), A = army[ty];
      w.spawnUnit(i, A.team, A, cv.QX[q], cv.QZ[q], cv.QR[q]);
      if (i < A.resStart) w.spawnedAlive[A.team]--; // only reserve slots count towards the troop cap
      switchTeam(cv, i, tm);
    } else {
      // a zombie rises in a free reserve slot of the zombie's side (its last group, see convertRules)
      const zt = army[cv.QA[q]].team; i = w.freeSlot(zt); if (i < 0) continue;
      w.spawnUnit(i, zt, army[typeOf(w, i)], cv.QX[q], cv.QZ[q], cv.QR[q]);
      if (tm !== zt) switchTeam(cv, i, tm);
    }
    startRise(cv, i, army[typeOf(w, i)].h);
    cv.inf[tm]++; cv.nDirty = true;
    fxPush(cv, KIND_RISE, i, X[i], w.GY[i], Z[i], cv.QK[q]);
  }
  // 2. climbing out of the ground
  for (let k = 0; k < cv.rN;) {
    const i = cv.RI[k];
    if (ALIVE[i] && !w.AIR[i] && w.Y[i] < 0) { w.Y[i] = Math.min(0, w.Y[i] + army[typeOf(w, i)].h * 0.85 / RISE_T * dt); if (w.Y[i] < 0) { k++; continue; } }
    cv.RI[k] = cv.RI[--cv.rN];
  }
  // 3. priests channel on their target instead of hitting it
  for (let a = 0; a < army.length; a++) {
    if (!cv.priest[a]) continue;
    const A = army[a];
    for (let i = A.start; i < A.end; i++) {
      if (!ALIVE[i]) continue;
      if (cv.CC[i] > 0) { cv.CC[i] -= dt; continue; }
      const t = TEAM[i], j = w.TGT[i];
      let ok = !w.isPu(i) && j >= 0 && !w.isPu(j) && ALIVE[j] && TEAM[j] !== t; // possessed units neither channel nor get converted
      const tj = ok ? typeOf(w, j) : 0, need = cv.chan[tj];
      if (ok) {
        const dx = X[j] - X[i], dz = Z[j] - Z[i], R = CHANNEL_RANGE + A.r + army[tj].r;
        ok = need > 0 && dx * dx + dz * dz <= R * R;
      }
      if (!ok) { cv.CH[i] = -1; continue; }
      cv.CH[i] = j;
      if (cv.CS[j] < cv.n - 1) cv.CP[j] = 0; // nobody prayed for it last tick: start over
      if (cv.CS[j] !== cv.n) { cv.CS[j] = cv.n; cv.CN[j] = 0; }
      const c = cv.CN[j]++;
      cv.CP[j] += c === 0 ? dt : c <= 3 ? dt * 0.25 : 0; // up to 1.75x faster with four or more priests
      // channelling: keep the target, no strikes, nearly stand still
      w.CD[i] = Math.max(w.CD[i], 0.2); w.RT[i] = Math.max(w.RT[i], 0.2); w.VX[i] *= 0.25; w.VZ[i] *= 0.25;
      if (cv.CP[j] >= need) {
        switchTeam(cv, j, t);
        cv.conv[t]++; cv.nDirty = true;
        cv.CH[i] = -1; cv.CC[i] = CONVERT_CD;
        fxPush(cv, KIND_CONVERT, j, X[j], w.GY[j], Z[j], t);
      }
    }
  }
  // 4. nobody keeps aiming at a new friend (units, pending strikes, building guns)
  if (cv.fix) {
    cv.fix = false;
    const TGT = w.TGT, PTGT = w.PTGT;
    for (let i = 0; i < w.N; i++) {
      if (!ALIVE[i]) continue;
      const g = TGT[i]; if (g >= 0 && TEAM[g] === TEAM[i]) TGT[i] = -1;
      const p = PTGT[i]; if (p >= 0 && TEAM[p] === TEAM[i]) PTGT[i] = -1;
    }
    const bg = w.bGuns(), BT = w.BT();
    for (let b = 0; b < bg.length; b++) for (const G of bg[b]) if (G.tg >= 0 && TEAM[G.tg] === BT[b]) G.tg = -1;
  }
}

/** send() hook: one 'convert' message per frame while something changed (output only, no effect on the state). */
export function convertSend(cv, gen) {
  const w = cv.w;
  let nb = 0, beams = null;
  for (let a = 0; a < w.army.length; a++) {
    if (!cv.priest[a]) continue;
    for (let i = w.army[a].start, e = w.army[a].end; i < e && nb < BEAMCAP; i++) if (cv.CH[i] >= 0 && w.ALIVE[i]) nb++;
  }
  if (!nb && !cv.beams && !cv.fxN && !cv.dirty && !cv.nDirty) return;
  if (nb) {
    beams = new Float32Array(nb * 3); let k = 0;
    for (let a = 0; a < w.army.length && k < nb; a++) {
      if (!cv.priest[a]) continue;
      for (let i = w.army[a].start, e = w.army[a].end; i < e && k < nb; i++) {
        const j = cv.CH[i]; if (j < 0 || !w.ALIVE[i]) continue;
        beams[k * 3] = i; beams[k * 3 + 1] = j; beams[k * 3 + 2] = Math.min(1, cv.CP[j] / (cv.chan[typeOf(w, j)] || 1)); k++;
      }
    }
  }
  const fx = cv.fx.slice(0, cv.fxN * 6), team = cv.dirty ? w.TEAM.slice() : null;
  const tr = [fx.buffer]; if (team) tr.push(team.buffer); if (beams) tr.push(beams.buffer);
  postMessage({ type: 'convert', gen, N: w.N, split: w.TA[1].start, groups: w.army.map(A => [A.team, A.start, A.end]), n: [cv.inf[0], cv.inf[1], cv.conv[0], cv.conv[1]], team, beams, fx }, tr);
  cv.fxN = 0; cv.dirty = false; cv.nDirty = false; cv.beams = nb;
}
